import { BasicReferencedEntityDataStringId } from "../api";

export type IsoDate = string;
export type IsoDateTime = string;
export type UniqueID = string;

/** Hospital Config type structure */
export type HospitalConfiguration = {
  haemoglobinRangeFemale: RangeValue;
  haemoglobinRangeMale: RangeValue;
  neutrophilRange: RangeValue;
  plateletRange: RangeValue;
  wbcRange: RangeValue;
  granulocyteRange?: RangeValue;
};

export enum PreferredContactMethod {
  Email = "Email",
  Phone = "Phone",
}

export type Measurement = {
  unit?: string;
  value: string;
  measurementInterval?: {
    id: string;
    outOfRange: boolean;
  };
};

interface User {
  id: string;
  firstName: string;
  lastName: string;
}

export interface PatientResult {
  whiteBloodCells: Measurement;
  granulocytes: Measurement;
  neutrophils: Measurement;
  haemoglobin: Measurement;
  platelets: Measurement;
  belowNeutrophilThreshold?: Maybe<boolean>;
  dateRecorded: IsoDateTime;
  daysAgo?: Maybe<number>;
  id: string;
  device?: BasicReferencedEntityDataStringId;
}

export type TestResult = PatientResult;

export type PatientResultsData = Array<TestResult>;

export type PatientNormalRanges = {
  wbcRange?: RangeValue;
  granulocyteRange?: RangeValue;
  neutrophilRange?: RangeValue;
  haemoglobinRange?: RangeValue;
  plateletRange?: RangeValue;
};

export enum Sex {
  Female = "Female",
  Male = "Male",
}

export type Device = {
  id: string;
};

export type Address = {
  countryCode: string;
  city: string;
  line1: string;
  line2?: Maybe<string>;
  state?: string;
  postcode: string;
};

export type ContactDetails = {
  address: Address;
  email: string;
  primaryContact: string;
  secondaryContact?: Maybe<string>;
  preferredContactMethod?: PreferredContactMethod;
};

export type ConsentUpdateInformation = {
  changeDate: string;
  changedBy?: Maybe<{
    firstName: string;
    lastName: string;
  }>;
};

export type Consent = {
  service: boolean;
  serviceLastUpdate: Array<ConsentUpdateInformation & { service: boolean }>;
  privacyPolicy: boolean;
  privacyPolicyLastUpdate: Array<ConsentUpdateInformation & { privacyPolicy: boolean }>;
  feedback: boolean;
  feedbackLastUpdate: Array<ConsentUpdateInformation & { feedback: boolean }>;
  research: boolean;
  researchLastUpdate: Array<ConsentUpdateInformation & { research: boolean }>;
};

export type PatientData = {
  contactDetails: ContactDetails;
  dateOfBirth: IsoDate;
  trainingDate?: Maybe<IsoDateTime>;
  disEnrollmentDate?: Maybe<IsoDateTime>;
  disEnrolledBy?: Maybe<User>;
  enrollmentDate?: Maybe<IsoDateTime>;
  enrolledBy?: Maybe<User>;
  firstName: string;
  hospitalNumber: string;
  id: UniqueID;
  patientId?: string; // Stable UUID for linking across document edits
  lastName: string;
  latestResult?: Maybe<TestResult>;
  lastMeasurementResultId?: string;
  deviceMeasurementIds?: string[];
  nhsNumber: string;
  oncologist?: Maybe<string>;
  sex: Sex;
  testResults?: Maybe<PatientResultsData>;
  consent: Consent;
  deliveryStatus?: "pending_device" | "withdraw" | "none";
  entiaPatientId?: string;
};

/**
 * Patient status task response types
 */
export interface PatientStatusResponse {
  action: "enrolled" | "withdrawn";
  patient_id: string;
  timestamp: string;
  performed_by: string;
}

export type PatientsListEntry = {
  latestResult?: Maybe<{
    whiteBloodCells: Measurement;
    granulocytes: Measurement;
    neutrophils: Measurement;
    haemoglobin: Measurement;
    platelets: Measurement;
    belowNeutrophilThreshold?: Maybe<boolean>;
    dateRecorded: IsoDateTime;
    daysAgo?: Maybe<number>;
    id: string;
  }>;
  lastMeasurementResultId?: string;
  deviceMeasurementIds?: string[];
} & Pick<
  PatientData,
  "id" | "firstName" | "lastName" | "hospitalNumber" | "nhsNumber" | "dateOfBirth" | "sex" | "entiaPatientId"
>;

/**
 * ExtraHorizon patient document structure (matches API response format)
 */
/* eslint-disable camelcase */
export interface ExhPatientData {
  id: string;
  status?: string;
  data?: {
    patient_id?: string; // Stable UUID across document edits
    name?: string;
    email?: string;
    nhs_number?: string;
    hospital_number?: string;
    date_of_birth?: string;
    sex?: string;
    entia_patient_id?: string;
    hospital?: string;
    phone_number?: string;
    enrollment_date?: string;
    enrolled_by?: string; // User ID string
    withdrawn_date?: string;
    withdrawn_by?: string; // User ID string
    oncologist?: string;
    trained?: boolean;
    trained_by?: string;
    address_line1?: string;
    address_line2?: string;
    address_city?: string;
    address_state?: string;
    address_postcode?: string;
    address_country_code?: string;
    secondary_contact?: string;
    preferred_contact_method?: string;
    last_measurement_result_id?: string;
    device_measurement_ids?: string[];
  };
}

/**
 * ExH patient document data structure for create/update operations
 */
export interface ExhPatientCreateData {
  name: string;
  email?: string;
  nhs_number: string;
  hospital_number: string;
  date_of_birth: string;
  sex: string;
  phone_number?: string;
  oncologist?: string;
  address_line1?: string;
  address_line2?: string;
  address_city?: string;
  address_state?: string;
  address_postcode?: string;
  address_country_code?: string;
  secondary_contact?: string;
  preferred_contact_method?: string;
  consent_service?: boolean;
  consent_privacy_policy?: boolean;
  consent_feedback?: boolean;
  consent_research?: boolean;
  creator_user_id?: string;
  creator_first_name?: string;
  creator_last_name?: string;
}
/* eslint-enable camelcase */
